<script>
import jsonRteToHtml from '@rei/rich-text-renderer';
import { getWindow } from '../../utils/browserVars';
import cmsContentYouTubeTracking from '../../utils/cmsContentEnrichmentYouTubeTracking';
import cmsContentEnrichmentLazyVideoLoad from '../../utils/cmsContentEnrichmentLazyVideoLoad';

export default {
  name: 'LongFormContent',
  props: {
    jsonContent: {
      required: true,
      type: Object,
    },
    addParagraphLabel: {
      required: false,
      type: String,
      default: '',
    },
  },
  data: () => ({
    htmlEnrichment: null,
    baseClassName: 'html-wysiwyg-content',
  }),
  computed: {
    updatedContent() {
      return jsonRteToHtml(this.jsonContent);
    },
  },
  watch: {
    content: {
      immediate: true,
      handler() {
        const browserWindow = getWindow();
        if (browserWindow) {
          browserWindow.clearTimeout(this.htmlEnrichment);
          this.htmlEnrichment = browserWindow.setTimeout(() => {
            cmsContentEnrichmentLazyVideoLoad(this.$refs.rawHtml);
            cmsContentYouTubeTracking(this.$refs.rawHtml);
          }, 300);
        }
      },
    },
  },
};
</script>
<template>
  <article
    v-if="updatedContent"
    ref="rawHtml"
    :class="
      [(addParagraphLabel) ? `${baseClassName}  test-results__inclusion` : `${baseClassName}`]
    "
    class="ea_img-responsive"
    data-ui="article-main-text-content"
    v-html="updatedContent"
  />
</template>
<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

  $baseClassName: 'html-wysiwyg-content';

  .test-results__inclusion {
    p:first-of-type::before {
      font-weight: bold;
      content: 'Test Results: '
    }
  }

  article.#{$baseClassName} {
    h2 {
      margin: 0.75em 0;
    }

    h3, h4 {
      margin: 1em 0;
    }

    p {
      margin: 1em 0;

      &:empty {
        margin: 0;
        padding: 0;
        line-height: 0;
      }
    }

    a {
      display: inline;
      vertical-align: baseline;

      &:empty {
        margin: 0;
        padding: 0;
        line-height: 0;
      }
    }

    @include cdr-xs-mq-only {
      iframe {
        width: 100%;
      }
    }

    .btn.btn-primary {
      margin-bottom: $cdr-space-one-x;
    }

    .text-center img {
      margin: 0 auto;
    }

    figure {
      margin: 0;
    }

    .p2 {
      font-weight: bold;
    }

    h2 {
      @include cdr-text-heading-serif-strong-800;
    }

    h3 {
      @include cdr-text-heading-sans-500;
      font-weight: 600;
    }

    h4 {
      @include cdr-text-heading-serif-300;
      font-weight: 600;
    }

    ul, ol {
      margin-left: $cdr-space-two-x;
      margin-bottom: $cdr-space-three-quarter-x;
    }

    li > ul li::before {
      content: "\2013";
    }

    li > p {
      display: inline;
    }

    ul[style*="list-style: none;"] > li::before,
    li.collapsibleList::before {
      content: none;
    }

    .collapsibleList li::before {
      content: "";
    }

    // related articles
    ul.related-articles-links {
      margin-left: 0;
      padding-left: 0;
    }

    ul.related-articles-links > li::before {
      content: none;
    }

    // Keep until all checklists have been refactored
    ul[style="margin-bottom: 0; margin-left: 3px; list-style-type:none;"] > li:before {
      content: none;
    }

    // checklist refactor styles - requires HTML clean up in Hippo
    .checklist-head {
      margin-top: $cdr-space-four-x;
    }

    .checklist-head .checklist-icon {
      display: inline-block;
      margin-right: $cdr-space-three-quarter-x;
      width: 50px;
      height: 50px;
    }

    .checklist-head hr {
      margin: $cdr-space-three-quarter-x 0;
      border-top: 3px solid #9c9c9f;
    }

    .checklist-head .checklist-heading {
      display: inline-block;
      margin-bottom: 0;
      vertical-align: bottom;
    }

    .checklist-subsection-heading {
      @include cdr-text-heading-sans-300;
      font-weight: 600;
    }

    .checklist-subsection-hr {
      border-top: 2px solid #e5e5e5;
      margin: $cdr-space-one-and-a-half-x 0;
    }

    @media (min-width: 768px) {
      .checklist-two-columns {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: $cdr-space-two-x $cdr-space-two-x;
      }
    }

    ul.checklist-items {
      list-style-type: none;
      margin: 0 0 0 $cdr-space-one-and-a-half-x;
      padding: 0;
    }

    ul.checklist-items > li:before {
      display: none;
    }

    .checkbox label, .checkbox label a {
      display: inline;
    }

    .checkbox input {
      width: 1.6rem;
      height: 1.6rem;
    }

    // alignment fix for updated HTML version
    .checkbox label {
      font-size: 1.6rem;
      line-height: 2.2rem;
      padding: 0 1.2rem .8rem 3.2rem;
      width: 100%;
      display: inline-block;
      margin: 0;
    }

    .checklist-row .checkbox label {
      position: relative;
      display: inline-block;
      padding-left: 2.4rem;
    }

    .checklist-row .checkbox input {
      position: absolute;
      left: 0;
      top: 0.3rem;
    }

    .rei-cms__video-responsive {
      position: relative;
      padding-bottom: 56.25%;
    }
  }

  .#{$baseClassName} .rei-cms__video-responsive,
  .rei-cms__video-responsive embed,
  .rei-cms__video-responsive iframe,
  .rei-cms__video-responsive object,
  .rei-cms__video-responsive video {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    height: 100%;
    width: 100%;
    border: 0;
  }

</style>
